import { useQuery } from "@apollo/client";
import { Calendar, ChevronLeft, ChevronRight, Clock, Download, Filter, Users } from "lucide-react";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { gql } from "@/gql";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";
import { DayDetailModal } from "./day-detail-modal";

// GraphQL query for monthly shifts
const GET_MONTHLY_SHIFTS = gql(`
  query GetMonthlyShifts($filters: ShiftFilterInput!) {
    getShifts(filters: $filters) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

interface MonthlyCalendarViewProps {
  className?: string;
}

interface DayCellProps {
  date: DateTime;
  shifts: any[];
  isCurrentMonth: boolean;
  isToday: boolean;
  onDayClick?: (date: DateTime) => void;
}

interface TimeBlock {
  hour: number;
  workerCount: number;
  intensity: number; // 0-1 for color intensity
}

// Component to render individual day cells with time blocks
function DayCell({ date, shifts, isCurrentMonth, isToday, onDayClick }: DayCellProps) {
  // Process shifts into hourly coverage blocks
  const timeBlocks = useMemo((): TimeBlock[] => {
    const blocks: TimeBlock[] = [];
    const hourCounts = new Map<number, number>();

    // Count workers for each hour
    shifts.forEach((shift) => {
      const [startHour] = shift.startTime.split(":").map(Number);
      const [endHour] = shift.endTime.split(":").map(Number);

      for (let hour = startHour; hour < endHour; hour++) {
        hourCounts.set(hour, (hourCounts.get(hour) || 0) + 1);
      }
    });

    // Find max workers for intensity calculation
    const maxWorkers = Math.max(...Array.from(hourCounts.values()), 1);

    // Create time blocks for business hours (8 AM to 8 PM)
    for (let hour = 8; hour <= 19; hour++) {
      const workerCount = hourCounts.get(hour) || 0;
      blocks.push({
        hour,
        workerCount,
        intensity: workerCount / maxWorkers,
      });
    }

    return blocks;
  }, [shifts]);

  const totalWorkers = shifts.length;
  const hasShifts = shifts.length > 0;

  const getIntensityColor = (intensity: number): string => {
    if (intensity === 0) return "bg-gray-100 dark:bg-gray-800";
    if (intensity <= 0.33) return "bg-blue-200 dark:bg-blue-900/50";
    if (intensity <= 0.66) return "bg-blue-400 dark:bg-blue-700/70";
    return "bg-blue-600 dark:bg-blue-500";
  };

  return (
    <div
      className={cn(
        "border border-border p-2 min-h-[120px] cursor-pointer transition-colors hover:bg-muted/50",
        !isCurrentMonth && "text-muted-foreground bg-muted/20",
        isToday && "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950/20"
      )}
      onClick={() => onDayClick?.(date)}
    >
      {/* Date header */}
      <div className="flex items-center justify-between mb-2">
        <span className={cn("text-sm font-medium", isToday && "text-blue-700 dark:text-blue-300")}>
          {date.day}
        </span>
        {hasShifts && (
          <Badge variant="secondary" className="h-5 px-1 text-xs">
            {totalWorkers}
          </Badge>
        )}
      </div>

      {/* Time blocks visualization */}
      {hasShifts ? (
        <div className="space-y-1">
          {/* Business hours visualization (8 AM - 8 PM) */}
          <div className="grid grid-cols-4 gap-0.5">
            {timeBlocks.slice(0, 12).map((block) => (
              <div
                key={block.hour}
                className={cn(
                  "h-3 rounded-sm transition-colors",
                  getIntensityColor(block.intensity)
                )}
                title={`${block.hour}:00 - ${block.workerCount} worker${block.workerCount !== 1 ? "s" : ""}`}
              />
            ))}
          </div>

          {/* Summary info */}
          <div className="text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>
                {Math.min(
                  ...(timeBlocks.filter((b) => b.workerCount > 0).map((b) => b.hour) || [8])
                )}
                :00 -
                {Math.max(
                  ...(timeBlocks.filter((b) => b.workerCount > 0).map((b) => b.hour + 1) || [17])
                )}
                :00
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-16 text-xs text-muted-foreground">
          No shifts
        </div>
      )}
    </div>
  );
}

// Month navigation header
function MonthHeader({
  currentMonth,
  onPreviousMonth,
  onNextMonth,
  onToday,
}: {
  currentMonth: DateTime;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
}) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-2xl font-bold">{currentMonth.toFormat("MMMM yyyy")}</h2>
        <p className="text-sm text-muted-foreground">Monthly shift coverage overview</p>
      </div>

      <div className="flex items-center gap-2">
        {/* Export and filter options */}
        <Button variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
        <Button variant="outline" size="sm">
          <Filter className="w-4 h-4 mr-2" />
          Filter
        </Button>

        {/* Navigation */}
        <div className="flex items-center gap-1 ml-2">
          <Button variant="outline" size="sm" onClick={onPreviousMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={onNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Legend component
function TimeCoverageLegend() {
  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Coverage Legend</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 bg-gray-100 dark:bg-gray-800 rounded-sm" />
            <span>No coverage</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 bg-blue-200 dark:bg-blue-900/50 rounded-sm" />
            <span>Light</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 bg-blue-400 dark:bg-blue-700/70 rounded-sm" />
            <span>Medium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 bg-blue-600 dark:bg-blue-500 rounded-sm" />
            <span>Full coverage</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Each block represents one hour. Color intensity shows number of workers scheduled.
        </p>
      </CardContent>
    </Card>
  );
}

// Main monthly calendar component
export function MonthlyCalendarView({ className = "" }: MonthlyCalendarViewProps) {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const [currentMonth, setCurrentMonth] = useState(() => DateTime.now().startOf("month"));
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);

  // Fetch shifts for the entire month
  const { data: shiftsData, loading: shiftsLoading } = useQuery(GET_MONTHLY_SHIFTS, {
    variables: {
      filters: {
        franchiseId: currentSalon?.franchiseId,
        startDate: currentMonth.startOf("month").toISODate(),
        endDate: currentMonth.endOf("month").toISODate(),
      },
    },
    skip: !currentSalon?.franchiseId,
  });

  // Generate calendar grid (6 weeks to cover all month layouts)
  const calendarDays = useMemo(() => {
    const startOfWeek = currentMonth.startOf("week");
    const days: DateTime[] = [];

    for (let i = 0; i < 42; i++) {
      // 6 weeks × 7 days
      days.push(startOfWeek.plus({ days: i }));
    }

    return days;
  }, [currentMonth]);

  // Group shifts by date
  const shiftsByDate = useMemo(() => {
    if (!shiftsData?.getShifts) return new Map();

    const grouped = new Map<string, any[]>();
    shiftsData.getShifts.forEach((shift) => {
      const dateKey = DateTime.fromISO(shift.shiftDate).toISODate() ?? shift.shiftDate;
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)?.push(shift);
    });

    return grouped;
  }, [shiftsData]);

  const handlePreviousMonth = () => {
    setCurrentMonth((prev) => prev.minus({ months: 1 }));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => prev.plus({ months: 1 }));
  };

  const handleToday = () => {
    setCurrentMonth(DateTime.now().startOf("month"));
  };

  const handleDayClick = (date: DateTime) => {
    setSelectedDate(date);
    setIsDayModalOpen(true);
  };

  const handleDayModalClose = () => {
    setIsDayModalOpen(false);
    setSelectedDate(null);
  };

  const handleNavigateToDay = (_date: DateTime) => {
    setIsDayModalOpen(false);
  };

  if (!currentSalon) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Please select a salon to view monthly calendar</p>
        </CardContent>
      </Card>
    );
  }

  if (shiftsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading monthly calendar...</div>
      </div>
    );
  }

  const today = DateTime.now();

  return (
    <div className={cn("space-y-4", className)}>
      <MonthHeader
        currentMonth={currentMonth}
        onPreviousMonth={handlePreviousMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
      />

      <TimeCoverageLegend />

      <Card>
        <CardContent className="p-6">
          {/* Day of week headers */}
          <div className="grid grid-cols-7 mb-4">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-0 border border-border rounded-md overflow-hidden">
            {calendarDays.map((date) => {
              const dateKey = date.toISODate() ?? date.toFormat("yyyy-MM-dd");
              const dayShifts = shiftsByDate.get(dateKey) || [];
              const isCurrentMonth = date.month === currentMonth.month;
              const isToday = date.hasSame(today, "day");

              return (
                <DayCell
                  key={dateKey}
                  date={date}
                  shifts={dayShifts}
                  isCurrentMonth={isCurrentMonth}
                  isToday={isToday}
                  onDayClick={handleDayClick}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Month summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Month Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-500" />
              <span className="text-sm">Total Shifts: {shiftsData?.getShifts?.length || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-green-500" />
              <span className="text-sm">Working Days: {shiftsByDate.size}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              <span className="text-sm">
                Avg. Workers/Day:{" "}
                {shiftsByDate.size > 0
                  ? Math.round(((shiftsData?.getShifts?.length || 0) / shiftsByDate.size) * 10) / 10
                  : 0}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Day detail modal */}
      {selectedDate && (
        <DayDetailModal
          isOpen={isDayModalOpen}
          onClose={handleDayModalClose}
          date={selectedDate}
          shifts={
            shiftsByDate.get(selectedDate.toISODate() ?? selectedDate.toFormat("yyyy-MM-dd")) || []
          }
          onNavigateToDay={handleNavigateToDay}
        />
      )}
    </div>
  );
}
